import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { SHOP_LOCATION } from '@/config/constants';
import { getPublicShopBranding } from '@/api/shop';
import { emptyShopRouteOrigin, projectShopRouteOrigin } from '@/utils/shopRouteOrigin';

const ShopSettingsContext = createContext(null);
const PUBLIC_FIELDS = ['name', 'address', 'street_address', 'street_address_2', 'city', 'state', 'zip_code', 'timezone', 'latitude', 'longitude', 'logo_id'];
export const SHOP_BRANDING_WAIT_MS = 15_000;
const sourceIdentity = () => JSON.stringify([
  window.lvbDesktop?.backendUrl || null, window.lvbDesktop?.localBackendUrl || null,
]);

function publicProjection(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || typeof value.name !== 'string' || typeof value.address !== 'string'
    || typeof value.timezone !== 'string') throw new Error('Invalid shop branding');
  const result = {};
  PUBLIC_FIELDS.forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return;
    const field = value[key];
    if (field !== null && (['latitude', 'longitude'].includes(key)
      ? typeof field !== 'number' || !Number.isFinite(field) : typeof field !== 'string')) {
      throw new Error('Invalid shop branding');
    }
    result[key] = field;
  });
  if (Object.hasOwn(value, 'route_origin')) result.route_origin = projectShopRouteOrigin(value.route_origin);
  return result;
}

export const useShopSettings = () => useContext(ShopSettingsContext);

export const ShopSettingsProvider = ({ children }) => {
  const [shopSettings, setShopSettings] = useState(null);
  const [brandingStatus, setBrandingStatus] = useState('loading');
  const [brandingError, setBrandingError] = useState('');
  const owner = useRef({ active: false, sequence: 0, controller: null, source: sourceIdentity() });
  const saved = useRef(null);

  const refreshBranding = useCallback(async ({ isCurrent = () => true } = {}) => {
    const cycle = owner.current;
    if (!cycle.active || !isCurrent()) return false;
    if (cycle.source !== sourceIdentity()) {
      cycle.sequence += 1; cycle.controller?.abort(); saved.current = null;
      setShopSettings(null); setBrandingStatus('error');
      setBrandingError('The connection source changed. Reload before refreshing shop information.');
      return false;
    }
    cycle.controller?.abort();
    const controller = new AbortController();
    cycle.controller = controller;
    const sequence = ++cycle.sequence;
    const owned = () => cycle.active && sequence === cycle.sequence;
    const current = () => owned() && cycle.source === sourceIdentity() && isCurrent();
    setBrandingStatus('loading'); setBrandingError('');
    let timer; let abort;
    try {
      const stopped = new Promise((_, reject) => {
        abort = () => reject(new Error('Shop branding request stopped'));
        controller.signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => controller.abort(), SHOP_BRANDING_WAIT_MS);
      });
      const response = await Promise.race([
        getPublicShopBranding({ signal: controller.signal, timeout: SHOP_BRANDING_WAIT_MS, suppressServerErrorNotification: true }),
        stopped,
      ]);
      if (!current()) return false;
      const projection = publicProjection(response);
      saved.current = projection;
      setShopSettings(projection); setBrandingStatus('ready'); setBrandingError('');
      return true;
    } catch {
      if (current()) {
        setBrandingStatus('error');
        setBrandingError('Shop information could not be refreshed. Previously loaded shop information remains in use; a route origin is unavailable until one has been loaded.');
      }
      return false;
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener('abort', abort);
      if (owned()) {
        cycle.controller = null;
        if (cycle.source !== sourceIdentity()) {
          saved.current = null; setShopSettings(null); setBrandingStatus('error');
          setBrandingError('The connection source changed. Reload before refreshing shop information.');
        } else if (!isCurrent()) {
          // A retired editor's refresh cannot leave the public provider loading.
          setBrandingStatus(saved.current ? 'ready' : 'error');
          setBrandingError(saved.current ? '' : 'Shop information has not been refreshed.');
        }
      }
    }
  }, []);

  useEffect(() => {
    const cycle = owner.current;
    cycle.active = true;
    void refreshBranding();
    return () => { cycle.active = false; cycle.sequence += 1; cycle.controller?.abort(); };
  }, [refreshBranding]);

  // Branding/printing compatibility only. Routing uses routeOrigin below.
  const shopAddress = (() => {
    if (!shopSettings) return SHOP_LOCATION.address;
    if (shopSettings.street_address && shopSettings.city) {
      const street = [shopSettings.street_address, shopSettings.street_address_2]
        .filter(Boolean).join(', ');
      return `${street}, ${shopSettings.city}, ${shopSettings.state} ${shopSettings.zip_code}`;
    }
    return shopSettings.address || SHOP_LOCATION.address;
  })();

  const shopLocation = {
    lat: shopSettings?.latitude ?? SHOP_LOCATION.lat,
    lng: shopSettings?.longitude ?? SHOP_LOCATION.lng,
    address: shopAddress,
  };
  const sameSource = owner.current.source === sourceIdentity();
  const routeOrigin = {
    ...(sameSource && shopSettings?.route_origin || emptyShopRouteOrigin()),
    source: owner.current.source,
    acquisition: sameSource ? brandingStatus : 'error',
    error: sameSource ? brandingError : 'The connection source changed. Reload before using shop routes.',
  };

  return (
    <ShopSettingsContext.Provider value={{ shopSettings, shopLocation, routeOrigin, brandingStatus, brandingError, refreshBranding }}>
      {children}
    </ShopSettingsContext.Provider>
  );
};
